import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';

import type { CartCalculationOrderResponse } from '~/api/Catalog';
import { MechanicType } from '~/api/ETA';
import FreeDeliveryProgressBar from '~/components/FreeDeliveryProgressBar/FreeDeliveryProgressBar';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import Panel from '~/components/Panel/Panel';
import Toggle from '~/components/Toggle/Toggle';
import { useGlobal } from '~/hooks/useGlobal';
import {
  cartStore,
  checkoutStore,
  mainStore,
  orderStore,
  userStore,
} from '~/stores';
import type DeliveryType from '~/stores/CheckoutStore/Delivery';
import findNearestAvailableSlot from '~/utils/findNearestAvailableSlot';
import { formatPriceWithCurrency } from '~/utils/formaters';

import CheckoutProductCard from '../CheckoutProductCard/CheckoutProductCard';
import DeliveryPanel from '../DeliveryPanel';

import styles from './ExpressDelivery.module.scss';

interface ExpressDeliveryProps {
  expressDeliveryEnabled: boolean | undefined;
  delivery: DeliveryType;
  order: CartCalculationOrderResponse;
}

const ExpressDelivery: FC<ExpressDeliveryProps> = ({
  expressDeliveryEnabled,
  delivery,
  order,
}) => {
  const expressMechanic =
    orderStore.etaCalculation?.warehouse?.availability?.deliveryMechanics?.[
      MechanicType.ON_DEMAND
    ];
  const expressIsAvailable = expressMechanic?.availableNow;
  const isDeliveryFree = order?.is_delivery_free;
  const freeDeliveryThreshold = order?.delivery_info.threshold;
  const deliveryItems = cartStore.selectedCartItems.filter(({ sku }) =>
    delivery.itemsIds.includes(sku),
  );
  const leftUntilFreeDelivery = mainStore.convertPenceToPounds(
    freeDeliveryThreshold - order?.paid_without_delivery,
  );
  const nearestAvailableSlot = findNearestAvailableSlot(
    checkoutStore.deliverySlots,
  );

  const { isMobile } = useGlobal();

  const handleToggleExpress = () => {
    checkoutStore.slotSelectionModalStore.setSelectedSlot(delivery, order);
    if (!expressDeliveryEnabled) {
      checkoutStore.slotSelectionModalStore.updateDelivery(
        undefined,
        undefined,
      );
    } else {
      if (nearestAvailableSlot) {
        checkoutStore.slotSelectionModalStore.updateDelivery(
          nearestAvailableSlot.slot.schedule_slot_id,
          nearestAvailableSlot.timestamp,
          true,
        );
      }
    }
  };

  if (!expressIsAvailable) {
    return null;
  }

  const list = expressDeliveryEnabled ? (
    <div className={styles.list}>
      {isMobile ? (
        <Carousel
          opts={{
            align: 'start' as const,
            slidesToScroll: 1,
            containScroll: 'trimSnaps' as const,
          }}
          dir={userStore.dir}
          key={userStore.dir}
          className={styles.carousel}
        >
          <CarouselContent>
            {deliveryItems.map((offer) => (
              <CarouselItem className={styles.slide} key={offer.images[0]}>
                <CheckoutProductCard key={offer.id} offer={offer} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      ) : (
        <div className={classes([styles.desktopCarousel, styles.carousel])}>
          {deliveryItems.map((offer) => (
            <CheckoutProductCard key={offer.id} offer={offer} />
          ))}
        </div>
      )}
      {!isDeliveryFree && expressDeliveryEnabled && (
        <FreeDeliveryProgressBar
          leftUntilFreeDelivery={`${formatPriceWithCurrency(
            leftUntilFreeDelivery,
          )}`}
          cssWidth={`${
            (order?.paid_without_delivery / freeDeliveryThreshold) * 100
          }%`}
          className={styles.freeDeliveryProgressBar}
          lineClassName={styles.freeDeliveryProgressBarLine}
        />
      )}
    </div>
  ) : null;

  return (
    <div className={styles.expressDelivery}>
      <Panel
        icon="express"
        additionalContent={list}
        classNames={classes([!expressDeliveryEnabled && styles.panelContainer])}
        contentClassNames={styles.panel}
      >
        <Toggle
          classNames={classes([
            styles.toggleContainer,
            !expressDeliveryEnabled && styles.toggleContainerDisabled,
          ])}
          onChange={handleToggleExpress}
          checked={!!expressDeliveryEnabled}
          disabled={!nearestAvailableSlot}
        >
          <DeliveryPanel
            type="express"
            showPrice={expressDeliveryEnabled}
            order={order}
            delivery={delivery}
          />
        </Toggle>
      </Panel>
    </div>
  );
};

export default observer(ExpressDelivery);
